import { type ParticipantDocument, ParticipantDocumentStatus } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { Async } from '@piccolohealth/util';
import React from 'react';
import { FaRegFilePdf } from 'react-icons/fa';
import { useGenerateParticipantDocumentPdfMutation } from '../../graphql/hooks/useGenerateParticipantDocumentPdfMutation';
import { useAppContext } from '../../hooks/useAppContext';

interface Props {
  participantDocument: ParticipantDocument;
}

export const ParticipantDocumentPdfMenuItem = (props: Props) => {
  const { participantDocument } = props;
  const { organization, promiseToast } = useAppContext();
  const mutation = useGenerateParticipantDocumentPdfMutation();

  const isDraft = participantDocument.status === ParticipantDocumentStatus.Draft;

  const onClick = React.useCallback(async () => {
    const promise = async () => {
      const resp = await mutation.mutateAsync({
        organizationId: organization.id,
        participantId: participantDocument.participantId,
        participantDocumentId: participantDocument.id,
      });
      await Async.delay(500);
      const { viewUrl, message } = resp.generateParticipantDocumentPdf;
      window.open(viewUrl || '', '_blank');
      return message;
    };

    await promiseToast(promise, {
      loading: {
        title: 'Generating PDF...',
      },
      success: (resp: string | null) => {
        if (!resp) {
          return {
            title: 'PDF successfully generated',
            duration: 2000,
          };
        }

        return {
          title: 'PDF generated with warnings',
          description: resp,
          duration: 8000,
        };
      },
      error: {
        title: 'Failed to generate PDF',
      },
    });
  }, [
    mutation,
    organization.id,
    participantDocument.participantId,
    participantDocument.id,
    promiseToast,
  ]);

  return (
    <ChakraV3.MenuItem onClick={onClick} value='generate-participant-document-pdf'>
      <FaRegFilePdf />
      {isDraft ? 'Generate draft PDF' : 'Generate PDF'}
    </ChakraV3.MenuItem>
  );
};
